import { useState, useEffect } from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import {
  Button,
  Typography,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material'
import { ColumnsType } from 'antd/es/table'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../Components/Cards/Card'
import DataTable from '../../Components/Datatable'
import Stats from '../../Components/Stats'
import TableHeading from '../../Components/TableHeading'
import { GET_USERS } from '../../Graphql/Queries'
import { DELETE_USER, USER_REPORT } from '../../Graphql/Mutations'
import { SuperAdminPaths } from '../../Routes/Paths'
import { UsersType, UserWithRolesOrgType } from '../../Types/QueriesType'
import { useDebouncingValue } from '../../Utils/useDebouncingValue'
import globals from '../../Constants/Globals'
import DeleteIcon from '@mui/icons-material/Delete'
import { Error } from '../../Types/MessageType'
import Message from '../../Components/Message'
import { AdminPermissions } from '../../Constants/Permissions'
import useUserPermissions from '../../hooks/useUserPermission'
import IosShareIcon from '@mui/icons-material/IosShare';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'; // Import the icon
import NoteAddIcon from '@mui/icons-material/NoteAdd'; // Import the icon

type UserProps = {
  showCreateUser?: boolean
  check?: boolean
  showViewDetails?: boolean
  formdata?: string[]
  tableFormItemName?: string
  setUserIdArray?: (val: any[]) => void
  users?: any[]
  selectedUsers?: any[]
  onCheckboxChange?: (userId: string) => void
}

const Users = ({
  showCreateUser = true,
  check = false,
  formdata = [],
  tableFormItemName,
  showViewDetails = true,
  setUserIdArray,
  users,
  selectedUsers = [],
  onCheckboxChange,
}: UserProps) => {
  const { CREATE_USER } = SuperAdminPaths
  const { permissions } = useUserPermissions()
  const { VIEW_CREATE_USER, VIEW_DELETE_USER } = AdminPermissions
  const client = useApolloClient()
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<any[]>([])
  const [totalPages, setTotalPages] = useState<any>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalUsers, setTotalUsers] = useState<any>(0)
  const [alert, setAlert] = useState<Error>({ type: 'warning', message: '' })
  const [open, setOpen] = useState(false)
  const [record, setRecord] = useState<any>(null)
  const [deleteUser] = useMutation(DELETE_USER)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNo, setPhoneNo] = useState('')
  const [role, setRole] = useState('')
  const [orgSearch, setOrgSearch] = useState('')

  const { debouncedValue: _name, debouncing: nameSearching } =
    useDebouncingValue(name)
  const { debouncedValue: _email, debouncing: emailSearching } =
    useDebouncingValue(email)
  const { debouncedValue: _phoneNo, debouncing: phoneNoSearching } =
    useDebouncingValue(phoneNo)
  const { debouncedValue: _role, debouncing: roleSearching } =
    useDebouncingValue(role)
  const { debouncedValue: _orgSearch, debouncing: organizationSearching } =
    useDebouncingValue(orgSearch)

  const { organization: authOrganization, userId } = useSelector((state: any) => state.auth)

  const { loading: usersLoading } = useQuery<UserWithRolesOrgType>(GET_USERS, {
    variables: {
      userId: userId,
      orgId: authOrganization,
      name: _name,
      email: _email,
      phoneNum: _phoneNo,
      role: _role,
      organizationName: _orgSearch,
      limit: globals.pageSize,
      page: currentPage,
    },
    skip: !!users,
    onCompleted(data) {
      if (data?.usersWithRolesByOrganizationId?.users.length > 0) {
        const users = data?.usersWithRolesByOrganizationId?.users;
        setTotalUsers(data.usersWithRolesByOrganizationId?.totalDocs);
        setTotalPages({
          currentPage: data?.usersWithRolesByOrganizationId?.currentPage,
          limit: globals.pageSize,
          totalDocs: data.usersWithRolesByOrganizationId?.totalDocs,
          totalPages: data.usersWithRolesByOrganizationId?.totalPages,
        });

        // Directly map the users data including organizationName
        const orgUsers = users.map((user: UsersType, index: number) => ({
          key: index,
          _id: user._id,
          name: user.name,
          email: user.email,
          phone: user.phone,
          organization: user.organization,
          organizationName: user.organizationName || 'N/A',
          roleID: user?.roles[0]?._id,
          roleCode: user?.roles[0]?.roleCode,
          roleName: user?.roles[0]?.roleName,
        }));
        setTableData(orgUsers);
      } else {
        setTableData([]);
        setTotalUsers(0);
      }
    },
  })

  useEffect(() => {
    if (users) {
      const orgUsers = users.map((user: UsersType, index: number) => {
        return {
          key: index,
          _id: user._id,
          name: user.name,
          email: user.email,
          phone: user.phone,
          organization: user.organization,
          roleID: user?.roles[0]?._id,
          roleCode: user?.roles[0]?.roleCode,
          roleName: user?.roles[0]?.roleName,
        }
      })
      setTableData(orgUsers)
    }
  }, [users])

  const handleClickOpen = (record: any) => {
    setRecord(record)
    setOpen(true)
  }

  const handleDelete = () => {
    handleDeleteUser(record)
    setOpen(false)
    setRecord(null)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleDeleteUser = (record: any): void => {
    deleteUser({
      variables: {
        id: record._id,
      },
      onCompleted(data) {
        const updatedTableData = tableData.filter(
          (item) => item._id !== record._id
        )
        setTableData(updatedTableData)
        setTotalUsers((totalUsers: number) => totalUsers - 1)
        setAlert({ type: 'success', message: 'User deleted successfully' })
        setTimeout(() => {
          setAlert({
            type: 'success',
            message: '',
          })
        }, globals.timeout)
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message })
        setTimeout(() => {
          setAlert({
            type: 'error',
            message: '',
          })
        }, globals.timeout)
      },
    })
  }

  const [exportUserReport, { loading: exportLoading }] =
    useMutation(USER_REPORT)

  const handleExportAnalytics = (userId: string) => {
    exportUserReport({
      variables: {
        userId: userId,
      },
      onCompleted(data) {
        if (data?.generateUserReport) {
          const link = document.createElement('a')
          link.href = data.generateUserReport
          link.setAttribute('download', `user-analytics-${userId}.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          setAlert({
            type: 'success',
            message: 'Analytics exported successfully',
          })
          setTimeout(() => {
            setAlert({
              type: 'success',
              message: '',
            })
          }, globals.timeout)
        }
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message })
        setTimeout(() => {
          setAlert({
            type: 'error',
            message: '',
          })
        }, globals.timeout)
      },
    })
  }

  const isLoading: boolean =
    usersLoading ||
    nameSearching ||
    emailSearching ||
    phoneNoSearching ||
    roleSearching ||
    organizationSearching ||
    exportLoading

  const columns: ColumnsType<any> = [
    {
      title: (
        <TableHeading
          heading='Name'
          setSearch={setName}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'name',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '1',
      width: "17%",
    },
    ...(showViewDetails ? [{
      title: (
        <TableHeading
          heading="Organization"
          setSearch={setOrgSearch}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: "organizationName",
      render: (_: unknown, record: { organizationName: string }) => (
        <Tooltip title={record.organizationName} arrow>
          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 180,
              display: "block",
            }}
          >
            {record.organizationName || "N/A"}
          </Typography>
        </Tooltip>
      ),
      key: "5",
      width: "15%",
    }] : []),
    {
      title: (
        <TableHeading
          heading='Email Id'
          setSearch={setEmail}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'email',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '2',
      width: "22%",
    },
    {
      title: (
        <TableHeading
          heading='PhoneNumber'
          setSearch={setPhoneNo}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'phone',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '3',
      width: 160,
    },
    {
      title: (
        <TableHeading
          heading='Role'
          setSearch={setRole}
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'roleName',
      render: (text, record) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {record.roleName || 'N/A'}
        </Typography>
      ),
      key: '4',
      width: 170,
    },
    {
      title: '',
      render: (_, record) =>
        showViewDetails && (
          <Box sx={{ display: "flex", gap: 1, whiteSpace: "nowrap" }}>
             {/* View Details Icon with Tooltip */}
             <Tooltip title="View Details" arrow>
              <IconButton
                onClick={() => navigate(record._id, { state: { userId: record._id } })} 
                sx={{
                  color: "rgb(250, 150, 157)", 
                }}
              >
                <RemoveRedEyeIcon sx={{ fontSize: "1.8rem" }} />
              </IconButton>
            </Tooltip>
            {/* Export Analytics Icon with Tooltip */}
            <Tooltip title="Export Analytics" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); handleExportAnalytics(record._id)}}
                sx={{
                  color: "rgb(250, 150, 157)",
                }}
              >
                <IosShareIcon sx={{ fontSize: "1.8rem" }}/>
              </IconButton>
            </Tooltip>
          </Box>
        ),
      key: '6',
    },
    ...(showViewDetails && permissions.includes(VIEW_DELETE_USER) ? [{
      title: '',
      render: (_: any, record: any) => (
        <IconButton
          sx={{ alignSelf: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation(); 
            handleClickOpen(record); 
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
      key: '7',
     
    }] : []),
  ]

  const handleRowClick = (record: any) => {
    navigate(`/users/${record._id}`, { state: { userId: record._id } });
  };

  return (
    <>
      {showCreateUser && (
        <Stats
          buttonText={
            <>
              Create New User <NoteAddIcon sx={{ color: 'white', ml: 1 }} />
            </>
          }
          text="Total Users"
          number={totalUsers}
          link={permissions.includes(VIEW_CREATE_USER) ? CREATE_USER : ''}
        />
      )}
      <Box flexGrow={1} sx={{ pr: 3 }}>
        {!!alert?.message ? (
          <Message
            type={alert?.type}
            message={alert.message}
            variant={'filled'}
          />
        ) : null}
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Card>
        <DataTable
          loading={isLoading}
          tableData={tableData} // Use the tableData state
          totalPages={totalPages}
          showCheck={check}
          columnData={columns}
          page={(cp) => {
            setCurrentPage(Number(cp))
          }}
          scrollX='max-content'
          formData={formdata}
          tableFormItemName={tableFormItemName}
          scrollY={450}
          onRow={(record) => handleRowClick(record)}
        />
      </Card>
    </>
  )
}

export default Users
